import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Download, Plus, Edit2, AlertTriangle, Lock } from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { WorkItemEditor, AddWorkItemButton } from '../components/WorkItemEditor';
import { UpgradeBanner } from '../components/UpgradeBanner';
import { FilterBar2 } from '../components/filters';

const StoriesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canEdit, isLowerTier, isStoryLayerEnabled } = useTenant();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New Filter Bar 2.0 state
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const status = searchParams.get('status');
    const feature_id = searchParams.get('feature_id');
    return {
      status: status ? status.split(',') : [],
      feature_id: feature_id ? [feature_id] : [],
    };
  });
  const [quickFilters, setQuickFilters] = useState({
    blocked_only: false,
    behind_only: false,
    at_risk_only: false,
    due_this_week: false,
  });
  const [filterOptions, setFilterOptions] = useState({ features: [], sprints: [] });
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [selectedFeatureForCreate, setSelectedFeatureForCreate] = useState(null);

  // Check if story layer is enabled
  const storyLayerEnabled = isStoryLayerEnabled();

  useEffect(() => { loadFilters(); }, []);
  
  // Reload stories when filters change
  useEffect(() => { 
    if (storyLayerEnabled) {
      loadStories();
      // Update URL with current filters
      const params = new URLSearchParams();
      if (advancedFilters.status?.length > 0) params.set('status', advancedFilters.status.join(','));
      if (advancedFilters.feature_id?.length > 0) params.set('feature_id', advancedFilters.feature_id[0]);
      setSearchParams(params, { replace: true });
    }
  }, [advancedFilters, quickFilters, storyLayerEnabled]);

  const loadFilters = async () => {
    try {
      const res = await api.getFilters();
      setFilterOptions({
        features: res.data.features || [],
        sprints: res.data.sprints || []
      });
    } catch (error) { console.error('Failed to load filters:', error); }
  };

  const loadStories = async () => {
    try {
      setLoading(true);
      const params = {};
      
      // Multi-select filters
      if (advancedFilters.status?.length > 0) params.status = advancedFilters.status.join(',');
      if (advancedFilters.feature_id?.length > 0) params.feature_id = advancedFilters.feature_id[0];
      if (advancedFilters.project_id?.length > 0) params.project_id = advancedFilters.project_id[0];
      
      // Quick filters override status
      if (quickFilters.blocked_only) params.status = 'Blocked';
      if (quickFilters.behind_only) params.status = 'Behind';
      if (quickFilters.at_risk_only) params.status = 'Behind,Due Date at Risk,Blocked';
      
      const res = await api.getStories(params);
      setStories(res.data.stories || []);
    } catch (error) { console.error('Failed to load stories:', error); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => {
    if (v === null || v === undefined || isNaN(v)) return '$0';
    return Math.abs(v) >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : Math.abs(v) >= 1e3 ? `$${(v/1e3).toFixed(1)}K` : `$${v.toFixed(0)}`;
  };

  const kpis = {
    total: stories.length,
    avgComplete: stories.length > 0 ? Math.round(stories.reduce((s, st) => s + (st.percent_complete || 0), 0) / stories.length) : 0,
    blocked: stories.filter(s => (s.blocked_count || 0) > 0).length,
    totalHours: stories.reduce((s, st) => s + (st.hours_act || 0), 0)
  };

  // Editor handlers
  const handleAddStory = () => {
    setEditingStory(null);
    const featureId = advancedFilters.feature_id?.[0] || (filterOptions.features[0]?.id);
    setSelectedFeatureForCreate(featureId);
    setShowEditor(true);
  };

  const handleEditStory = (story) => {
    setEditingStory(story);
    setSelectedFeatureForCreate(story.feature_id);
    setShowEditor(true);
  };

  const handleEditorSave = () => {
    loadStories();
  };

  const handleEditorDelete = (id) => {
    setStories(stories.filter(s => s.id !== id));
  };

  return (
    <div>
      {/* Story Layer Disabled Warning */}
      {!storyLayerEnabled && (
        <div className="bg-[var(--clr-sev-atrisk)] bg-opacity-10 border border-amber-200 rounded-xl p-8 text-center mb-6">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[var(--clr-sev-atrisk)]" />
          </div>
          <h2 className="text-xl font-bold text-[var(--clr-text-primary)] mb-2">Story Layer Disabled</h2>
          <p className="text-[var(--clr-text-secondary)] mb-4 max-w-md mx-auto">
            Stories are disabled for your tenant. Your hierarchy is: Epic → Feature → Task.
            <br />Contact your administrator to enable the Story layer if needed.
          </p>
          <p className="text-sm text-[var(--clr-text-muted)]">
            Tasks can be created directly under Features from the Tasks page.
          </p>
        </div>
      )}
      
      {storyLayerEnabled && (
        <>
          {isLowerTier() && <UpgradeBanner feature="in-app story editing" className="mb-6" />}
          
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
                <FileText className="w-7 h-7 text-[var(--clr-brand-primary)]" />
                Stories
              </h1>
              <p className="text-[var(--clr-text-secondary)] mt-1">Track all stories across the hierarchy</p>
            </div>
            <div className="flex items-center gap-3">
              <AddWorkItemButton itemType="story" onClick={handleAddStory} />
              <a href={api.exportCSV('stories', { 
                ...(advancedFilters.status?.length > 0 ? { status: advancedFilters.status.join(',') } : {}),
                ...(advancedFilters.feature_id?.length > 0 ? { feature_id: advancedFilters.feature_id[0] } : {}),
              })} className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium" data-testid="export-csv-btn">
                <Download className="w-4 h-4" /> Export CSV
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Stories</p><p className="text-2xl font-bold">{kpis.total}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Avg % Complete</p><p className="text-2xl font-bold text-[var(--clr-brand-primary)]">{kpis.avgComplete}%</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">With Blockers</p><p className="text-2xl font-bold text-purple-600">{kpis.blocked}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Hours</p><p className="text-2xl font-bold text-[var(--clr-text-primary)]">{kpis.totalHours.toLocaleString()}</p></div>
      </div>

      {/* Filter Bar 2.0 */}
      <FilterBar2
        pageType="stories"
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        quickFilters={quickFilters}
        onQuickFiltersChange={setQuickFilters}
        counts={{
          blocked: kpis.blocked,
          behind: stories.filter(s => s.status === 'Behind').length,
          atRisk: stories.filter(s => ['Behind', 'Due Date at Risk', 'Blocked'].includes(s.status)).length,
        }}
        showInsightsBar={true}
        className="mb-6"
      />

      <div className="bg-[var(--clr-surface-card)] rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div></div>
        ) : stories.length === 0 ? (
          <div className="text-center py-12"><FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-[var(--clr-text-secondary)]">No stories found</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--clr-surface-subtle)] border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Story</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Feature / Epic</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Project</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Sprint</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Owner</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Tasks</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Hours</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stories.filter(Boolean).map((s, idx) => {
                  const isMatchedItem = s._is_matched === true;
                  const isParentContext = s._is_parent === true && !s._is_matched;
                  
                  return (
                  <tr 
                    key={s.id || idx} 
                    className={`hover:bg-[var(--clr-surface-hover)] ${isMatchedItem ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''}`}
                    data-testid={`story-row-${idx}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isMatchedItem && <span className="px-1.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">Match</span>}
                        <span className="font-medium text-[var(--clr-text-primary)] max-w-xs truncate">{s.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]"><div>{s.feature_name || '-'}</div><div className="text-xs text-[var(--clr-text-muted)]">{s.epic_name}</div></td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{s.project_name}</td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="w-12 bg-gray-200 rounded-full h-1.5"><div className="bg-[var(--clr-brand-primary)] h-1.5 rounded-full" style={{ width: `${s.percent_complete || 0}%` }}></div></div><span className="text-xs">{s.percent_complete || 0}%</span></div></td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{s.sprint || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{s.owner || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{s.task_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{s.hours_act || 0}/{s.hours_est || 0}</td>
                    <td className="px-4 py-3"><span className={`text-sm ${(s.variance || 0) > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]'}`}>{formatCurrency(s.act_cost)}</span></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}

      {/* Work Item Editor Modal */}
      {storyLayerEnabled && (
        <WorkItemEditor
          isOpen={showEditor}
          onClose={() => { setShowEditor(false); setEditingStory(null); }}
          itemType="story"
          item={editingStory}
          parentId={selectedFeatureForCreate}
          projectId={editingStory?.project_id || stories[0]?.project_id || advancedFilters.project_id?.[0]}
          onSave={handleEditorSave}
          onDelete={handleEditorDelete}
        />
      )}
    </div>
  );
};

export default StoriesPage;
